import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import { toast } from "react-toastify";
import { api, setAuthToken } from "../../services/api.js";
import { useAuth } from "../../context/AuthContext.jsx";
import { parseJwt } from "../../utils/jwt";
import { getPathForRole } from "../../utils/roleRouting";

export default function Reset() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();

  const initialToken = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return (sp.get("token") || sp.get("reset_token") || sp.get("t") || "").trim();
  }, [location.search]);

  const [token, setToken] = useState(initialToken);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);

  useEffect(() => { if (initialToken && !token) setToken(initialToken); }, [initialToken, token]);

  // Siivoa token pois URL:ista heti (ei jää historiaan eikä vuoda referrereihin)
  useEffect(() => {
    if (!initialToken) return;
    const url = new URL(window.location.href);
    ["token","reset_token","t"].forEach(k => url.searchParams.delete(k));
    window.history.replaceState({}, "", url.toString());
  }, [initialToken]);

  const labelBase =
    "block font-customFont1 text-sm uppercase tracking-wide text-black/70 dark:text-white/80 mb-1";
  const inputBase =
    "w-full h-11 rounded-lg px-3 font-customFont1 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-pink/60 dark:focus:ring-primary text-base";

  const tokenMissing = token.length === 0;
  const pwdTooShort = pwd.trim().length < 8;
  const mismatch = pwd !== pwd2;

  const disabled = loading || tokenMissing || pwdTooShort || mismatch;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    setLoading(true);
    try {
      const res = await api.auth.passwordReset(token, pwd);

      // Jos backend palauttaa tokenin, kirjataan heti sisään.
      const maybeToken = res?.token;
      if (maybeToken) {
        setAuthToken(maybeToken);
        try { await refresh?.(); } catch {}
        // ohjaa roolin mukaan
        const role = (() => { try { return parseJwt(maybeToken)?.role || "performer"; } catch { return "performer"; }})();
        const defaultPath = getPathForRole(role);
        toast.success("Salasana vaihdettu! Olet kirjautunut sisään.", { containerId: "global" });
        navigate(defaultPath, { replace: true });
        return;
      }

      // Muussa tapauksessa ohjataan kirjautumiseen
      toast.success("Salasana vaihdettu! Kirjaudu sisään uudella salasanalla.", { containerId: "global" });
      navigate("/kirjaudu", { replace: true, state: { resetOk: true } });
    } catch (err) {
      const code = String(err?.payload?.error || err?.message || "").toLowerCase();
      const msg =
        code.includes("expired") || code.includes("invalid")
          ? "Palautuslinkki on vanhentunut tai virheellinen. Pyydä uusi linkki."
          : (err?.payload?.message || err?.message || "Salasanan vaihto epäonnistui.");
      toast.error(String(msg), { containerId: "global" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-lemon/60 dark:bg-black/40">
      <div
        className={[
          "mx-auto max-w-6xl px-4 pb-12",
          "pt-24",
          "md:min-h-[calc(100vh-96px)] md:grid md:place-items-center",
        ].join(" ")}
      >
        <div className="w-full md:max-w-4xl lg:max-w-5xl mx-auto">
          <h1 className="text-2xl font-bold text-left md:text-center text-black/80 dark:text-white/90 mb-3">
            Aseta uusi salasana
          </h1>

          <div
            className={[
              "grid grid-cols-1 gap-6",
              "md:grid-cols-2 md:items-start md:justify-center",
            ].join(" ")}
          >
            {/* Lomake */}
            <div className="order-2 md:order-2">
              <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/60 backdrop-blur-md shadow-lg w-full p-4 sm:p-5">
                <form onSubmit={onSubmit} className="space-y-4" noValidate>
                  {/* Token piilotettuna URL:sta – näytä kenttä jos ei tullut queryssä */}
                  {tokenMissing && (
                    <div>
                      <label className={labelBase} htmlFor="token">Palautuslinkin token</label>
                      <input
                        id="token"
                        type="text"
                        value={token}
                        onChange={(e) => setToken(e.target.value.trim())}
                        className={inputBase}
                        required
                      />
                      <p className="mt-1 text-xs text-black/60 dark:text-white/60">
                        Liitä tähän sähköpostissa saamasi palautuslinkin token, jos sitä ei tullut URL:iin.
                      </p>
                    </div>
                  )}

                  <div>
                    <label className={labelBase} htmlFor="pwd">Uusi salasana</label>
                    <div className="relative">
                      <input
                        id="pwd"
                        type={showPwd ? "text" : "password"}
                        value={pwd}
                        onChange={(e) => setPwd(e.target.value)}
                        className={inputBase + " pr-10"}
                        autoComplete="new-password"
                        required
                        aria-invalid={pwdTooShort ? "true" : "false"}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-sm opacity-70 hover:opacity-100"
                        aria-label={showPwd ? "Piilota salasana" : "Näytä salasana"}
                      >
                        {showPwd ? "Piilota" : "Näytä"}
                      </button>
                    </div>
                    {pwdTooShort && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                        Vähintään 8 merkkiä.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className={labelBase} htmlFor="pwd2">Uusi salasana uudelleen</label>
                    <div className="relative">
                      <input
                        id="pwd2"
                        type={showPwd2 ? "text" : "password"}
                        value={pwd2}
                        onChange={(e) => setPwd2(e.target.value)}
                        className={inputBase + " pr-10"}
                        autoComplete="new-password"
                        required
                        aria-invalid={mismatch ? "true" : "false"}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd2((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-sm opacity-70 hover:opacity-100"
                        aria-label={showPwd2 ? "Piilota salasana" : "Näytä salasana"}
                      >
                        {showPwd2 ? "Piilota" : "Näytä"}
                      </button>
                    </div>
                    {mismatch && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                        Salasanat eivät täsmää.
                      </p>
                    )}
                  </div>

                  <NeonButton
                    type="submit"
                    variant="primary"
                    size="md"
                    className="w-full"
                    disabled={disabled}
                  >
                    {loading ? "Vaihdetaan…" : "Vaihda salasana"}
                  </NeonButton>

                  <p className="text-xs text-black/60 dark:text-white/60">
                    Kun vaihto onnistuu, ohjaamme sinut automaattisesti sisään tai kirjautumissivulle.
                  </p>
                </form>

                <p className="mt-6 text-center text-sm text-black/60 dark:text-white/60">
                  Muistitko kuitenkin salasanan?{" "}
                  <Link to="/kirjaudu" className="text-pink underline hover:opacity-80 dark:text-primary">
                    Kirjaudu sisään
                  </Link>
                  .
                </p>
              </div>
            </div>

            {/* Info/CTA */}
            <div className="order-1 md:order-1">
              <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 backdrop-blur p-4">
                <p className="text-black/80 dark:text-white/80 text-sm leading-relaxed md:text-center">
                  Avaa sähköpostissa saamasi palautuslinkki. Jos token ei tullut URL:iin, liitä se tähän sivulle.
                  Valitse uusi, vähintään 8 merkin pituinen salasana.
                </p>
                <div className="mt-4 flex justify-center">
                  <NeonButton as={Link} to="/" variant="ghost" size="sm">
                    Palaa etusivulle
                  </NeonButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
}
